import Vue from 'vue';
import VueRouter from 'vue-router';
import { RouteConfigSingleView, RouteRecord } from 'vue-router/types/router';
import { Action } from '../AppAbility';
import { ability } from '../lib/casl';
import { api } from '../plugins/salut-api';
import store from '../store';
import { AppActionKeys } from '../store/modules/app';
import { AuthActionKeys, AuthGetterKeys } from '../store/modules/auth';
import { NotificationActionKeys } from '../store/modules/notification';
import { ProfileActionKeys, ProfileGetterKeys } from '../store/modules/profile';
import { Breadcrumb } from '../types';
import { eventsEditRoutes, EventsRouteName, eventsRoutes } from './events';
import {
	eventsBreadcrumbs,
	contactsBreadcrumbs,
	formsBreadcrumbs,
	ngoBreadcrumbs,
	paymentBreadcrumbs,
	programBreadcrumbs,
	reportBreadcrumbs,
	systemBreadcrumbs,
	jobBreadcrumbs,
	otherBreadcrumbs,
} from './breadcrumb';
import { ContactsRouteName, contactsRoutes } from './contacts';
import { FormsRouteName, formsRoutes } from './forms';
import { NgoRouteName, ngoRoutes } from './ngo';
import { PaymentRouteName, paymentRoutes } from './payment';
import {
	programsRoutes,
	programsEditRoutes,
	programProposalsEditRoutes,
	ProgramsRouteName,
	programEvaluationsEditRoutes,
} from './program';
import { SystemRouteName, systemRoutes } from './system';
import { jobEditRoutes, JobRouteName, jobRoutes } from './job';
import { RouteName } from './route-name';
import { ReportsRouteName, reportsRoutes } from './report';

Vue.use(VueRouter);

export const breadcrumbs: Map<string, Breadcrumb> = new Map([
	...contactsBreadcrumbs,
	...jobBreadcrumbs,
	...eventsBreadcrumbs,
	...paymentBreadcrumbs,
	...formsBreadcrumbs,
	...ngoBreadcrumbs,
	...systemBreadcrumbs,
	...programBreadcrumbs,
	...reportBreadcrumbs,
	...otherBreadcrumbs,
]);

export interface AppRouteConfig extends RouteConfigSingleView {
	meta?: {
		breadcrumbs?: any[];
		caslAction?: Action; // missing this means publicly accessible
		caslSubject?: string; // missing this means publicly accessible
		feature?: string; // missing this means available to all
		[key: string]: any;
	};
	children?: AppRouteConfig[];
}

export const PERMISSION_DENIED_PATH = '/permission-denied';

const routes: Array<AppRouteConfig> = [
	{
		path: '/',
		component: () => import('../views/Main.vue'),
		meta: {
			breadcrumbs: [{ name: 'Home' }],
		},
		children: [
			{
				path: '',
				name: RouteName.home,
				component: () => import('../views/home/index.vue'),
			},
			{
				path: '/my-program-items',
				name: RouteName.myProgramItems,
				component: () => import('../views/home/MyProgramItems.vue'),
				meta: {
					breadcrumbs: [
						breadcrumbs.get('home'),
						breadcrumbs.get('myProgramItems'),
					],
				},
			},
			{
				path: '/inbox',
				component: { render: c => c('router-view') },
				children: [
					{
						path: '',
						name: RouteName.inbox,
						component: () => import('../views/inbox/index.vue'),
						meta: {
							breadcrumbs: [breadcrumbs.get('home'), breadcrumbs.get('inbox')],
						},
					},
					{
						path: 'notification-settings',
						name: RouteName.notificationSettings,
						component: () => import('../views/inbox/NotificationSettings.vue'),
						meta: {
							breadcrumbs: [
								breadcrumbs.get('home'),
								breadcrumbs.get('inbox'),
								breadcrumbs.get('notification-settings'),
							],
						},
					},
				],
			},
			{
				path: PERMISSION_DENIED_PATH,
				component: () => import('../views/PermissionDeniedPage.vue'),
			},

			{
				path: '/profile',
				name: RouteName.profile,
				component: () => import('../views/Profile.vue'),
				meta: {
					breadcrumbs: [breadcrumbs.get('home'), breadcrumbs.get('profile')],
				},
			},
			{
				path: '/change-password',
				name: RouteName.changePassword,
				component: () => import('../views/ChangePassword.vue'),
				// beforeEnter: ifNotAuthenticated,
				meta: {
					breadcrumbs: [
						breadcrumbs.get('home'),
						breadcrumbs.get('changePassword'),
					],
				},
			},

			{
				path: '/onboarding',
				name: RouteName.onboarding,
				component: () =>
					import('../views/onboarding/OnboardingEmbedded.vue'),
				meta: {
					breadcrumbs: [
						breadcrumbs.get('home'),
						breadcrumbs.get('onboarding'),
					],
				},
			},

			{
				path: '/contacts',
				name: ContactsRouteName.contact,
				component: {
					render: c => c('router-view'),
				},
				meta: {
					caslSubject: 'contacts',
					caslAction: 'view',
				},
				children: contactsRoutes(),
			},

			{
				path: '/payment',
				name: PaymentRouteName.payment,
				component: { render: c => c('router-view') },
				meta: {
					caslSubject: 'payments',
					caslAction: 'view',
				},
				children: paymentRoutes(),
			},
			{
				path: '/forms',
				name: FormsRouteName.forms,
				component: { render: c => c('router-view') },
				meta: {
					caslSubject: 'forms',
					caslAction: 'view',
					feature: 'forms',
				},
				children: formsRoutes(),
			},

			{
				path: '/ngo',
				name: NgoRouteName.ngo,
				component: { render: c => c('router-view') },
				meta: {
					caslSubject: 'organisation',
					caslAction: 'view',
				},
				children: ngoRoutes(),
			},

			{
				path: '/jobs',
				name: JobRouteName.jobs,
				component: { render: c => c('router-view') },
				meta: {
					caslSubject: 'jobs',
					caslAction: 'view',
				},
				children: jobRoutes(),
			},

			{
				path: '/events',
				name: EventsRouteName.events,
				component: { render: c => c('router-view') },
				meta: {
					caslSubject: 'events',
					caslAction: 'view',
				},
				children: eventsRoutes(),
			},

			{
				path: '/system',
				name: SystemRouteName.system,
				component: { render: c => c('router-view') },
				meta: {
					caslSubject: 'events',
					caslAction: 'view',
				},
				children: systemRoutes(),
			},
			{
				path: '/programs',
				name: ProgramsRouteName.program,
				component: { render: c => c('router-view') },
				children: programsRoutes(),
			},
			{
				path: '/reports',
				name: ReportsRouteName.report,
				component: { render: c => c('router-view') },
				children: reportsRoutes(),
			},
		],
	},
	{
		path: '/jobs',
		name: JobRouteName.editJobMain,
		component: () => import('../views/job/JobEditMain.vue'),
		meta: {
			caslSubject: 'jobs',
			caslAction: 'edit',
			feature: 'jobs',
		},
		children: jobEditRoutes(),
	},
	{
		path: '/events',
		name: EventsRouteName.editEventMain,
		component: () => import('../views/event/EventEditMain.vue'),
		meta: {
			caslSubject: 'events',
			caslAction: 'edit',
			feature: 'events',
		},
		children: eventsEditRoutes(),
	},
	{
		path: '/programs',
		name: ProgramsRouteName.editProgramMain,
		component: () => import('../views/program/ProgramEditMain.vue'),
		meta: {
			feature: 'programs',
		},
		children: programsEditRoutes(),
	},
	{
		path: '/programs/:programId/proposals',
		name: ProgramsRouteName.editProposalMain,
		component: () => import('../views/program/proposal/ProposalEditMain.vue'),
		meta: {
			feature: 'programs',
		},
		children: programProposalsEditRoutes(),
	},
	{
		path: '/programs/:programId/proposals/:proposalId/',
		name: ProgramsRouteName.editProposalMain,
		component: () =>
			import('../views/program/evaluation/EvaluationEditMain.vue'),
		meta: {
			feature: 'programs',
		},
		children: programEvaluationsEditRoutes(),
	},
	{
		path: '/signup',
		name: RouteName.signup,
		component: () => import('../views/signup/Signup.vue'),
		children: [
			{
				path: 'basic',
				name: RouteName.signupBasic,
				component: () => import('../views/signup/SignupBasic.vue'),
			},
			{
				path: 'email',
				name: RouteName.signupEmail,
				component: () => import('../views/signup/SignupEmailVerification.vue'),
			},
			{
				path: 'continue',
				name: RouteName.signupExtra,
				component: () => import('../views/signup/SignupExtra.vue'),
			},
		],
	},
	{
		path: '/login',
		name: RouteName.login,
		component: () => import('../views/Login.vue'),
		// beforeEnter: ifNotAuthenticated,
	},
	{
		path: '/forget-password',
		name: RouteName.forgetPassword,
		component: () => import('../views/ForgetPassword.vue'),
		// beforeEnter: ifNotAuthenticated,
	},
	{
		path: '/staff-welcome',
		name: RouteName.staffWelcome,
		component: () => import('../views/ResetPassword.vue'),
		props: { mode: 'staff-welcome' },
	},
	{
		path: '/reset-password',
		name: RouteName.resetPassword,
		component: () => import('../views/ResetPassword.vue'),
		props: { mode: 'reset-password' },
	},
	{
		path: '/reset-password-success',
		name: RouteName.resetPasswordSuccess,
		component: () => import('../views/ResetPasswordSuccess.vue'),
	},
	{
		path: '/consent',
		name: RouteName.jobApplicationConsent,
		component: () => import('../views/job/JobApplicationConsent.vue'),
	},
	{
		path: '/consent-terms',
		name: RouteName.jobApplicationConsentTerms,
		component: () => import('../views/job/JobApplicationConsentTerms.vue'),
	},
	{
		path: '/proposal-endorsement',
		name: RouteName.proposalEndorsement,
		component: () => import('../views/program/ProposalEndorsement.vue'),
	},
	{
		path: '/request-file',
		name: RouteName.requestFile,
	},
	{
		path: '*',
		redirect: '/',
	},
];

const router = new VueRouter({
	mode: 'history',
	base: process.env.BASE_URL,
	routes,
});

export const getCaslDataFromRoutes = (
	matchedRoutes: RouteRecord[],
): {
	caslSubject: string;
	caslAction: Action;
	feature: string;
} => {
	let caslSubject;
	let caslAction;
	let feature;
	//  matched routes are ordered from parent to children,
	//  so we loop over them and get the last CASL data (which mean child's data will override parent's)
	for (const route of matchedRoutes) {
		caslSubject = route.meta?.caslSubject || caslSubject;
		caslAction = route.meta?.caslAction || caslAction;
		feature = route.meta?.feature || feature;
	}
	return {
		caslSubject,
		caslAction,
		feature,
	};
};
//it can crash before page load
router.onError(error => {
	if (/loading chunk .+ failed/i.test(error.message)) {
		console.error('missing chunk error detected');
		store.dispatch(`app/${AppActionKeys.forceReloadIfVersionUpdated}`);
	}
});

router.beforeEach(async (to, from, next) => {
	//check if version updated (potential chunk missing) before actual error happens
	store.dispatch(`app/${AppActionKeys.forceReloadIfVersionUpdated}`);

	const publicPages = [
		'Login',
		'ForgetPassword',
		'ResetPassword',
		'StaffWelcome',
		'ResetPasswordSuccess',
		'Signup',
		'SignupBasic',
		'JobApplicationConsent',
		'JobApplicationConsentTerms',
		'ProposalEndorsement',
	];

	const authenticatedUserBlockedPaths = ['/login', '/forget-password'];

	store.dispatch(`app/${AppActionKeys.setPreviousPage}`, { page: from });

	/**
	 * global processing
	 */
	// process language query
	if (to.query.lang) {
		await store.dispatch(`app/${AppActionKeys.setUrlQueryLang}`, {
			lang: to.query.lang,
		});
		// remove `lang` from query param and keep others
		next({
			path: to.path,
			query: Object.entries(to.query)
				.filter(([key]) => key !== 'lang')
				.reduce((prev, [key, val]) => ({ ...prev, [key]: val }), {}),
		});
	}

	// dynamically change the "from" route in breadcrumbs
	// 2022-11-08: let's deprecate this logic, and use Breadcrumbs.vue instead
	if (to.meta.dynamicFrom && from.meta.breadcrumbs) {
		const dynaIdx = to.meta.dynamicFrom;

		const fromIdx = from.meta.breadcrumbs.length > dynaIdx ? dynaIdx : 0;

		if (dynaIdx >= 1 && fromIdx >= 1) {
			const breadCrumb = from.meta.breadcrumbs[fromIdx];
			const path =
				fromIdx === from.meta.breadcrumbs.length - 1
					? from.path
					: breadCrumb.to;

			to.meta.breadcrumbs[dynaIdx] = {
				name: breadCrumb.name,
				to: path,
				i18nKey: breadCrumb.i18nKey,
			};
		}
	}

	/**
	 * Authentication related processing
	 */
	const isLoggedIn = store.getters[`auth/${AuthGetterKeys.isAuthenticated}`]();
	if (isLoggedIn) {
		// Check permission
		// this action is sync, and with duplicate calling prevention logic inside
		store.dispatch(`auth/${AuthActionKeys.initAccessTokenFromLocalStorage}`);
		// only check when access token exists because abilities are decoded from the token.
		// otherwise will be handled next by matching against `publicPages`
		const { caslSubject, caslAction, feature } = getCaslDataFromRoutes(
			to.matched,
		);
		if (feature) {
			if (!Vue.$feature(feature)) {
				router.push({ path: PERMISSION_DENIED_PATH });
				return;
			}
		}
		if (caslSubject && caslAction) {
			if (ability.cannot(caslAction, caslSubject)) {
				router.push({ path: PERMISSION_DENIED_PATH });
				return;
			}
		}

		// load profile
		if (
			// not loading
			!store.getters[`profile/${ProfileGetterKeys.loading}`] &&
			// not loaded already
			!store.getters[`profile/${ProfileGetterKeys.basic}`]?.ngoID &&
			// path is email verification, do not load profile.
			// profile will be loaded after verification succeeds
			!(to.path === '/signup' && to.query.email && to.query.code)
		) {
			store.dispatch(`profile/${ProfileActionKeys.fetch}`);
		}
		if (typeof to.name == 'string') {
			if (to.name === 'RequestFile') {
				const filePath = to.query['contactFilePath'] as string;
				const file = await api.getContactFileSignedUrl(filePath);
				window.location = file; //quit app
			}
			// badge update
			if (!publicPages.includes(to.name)) {
				store.dispatch(`notification/${NotificationActionKeys.fetch}`);
			}
		}
		if (authenticatedUserBlockedPaths.includes(to.path)) {
			next({ name: 'Home', query: to.query });
		}
	} else {
		// not logged in. This should be distinquished from session expired
		if (typeof to.name === 'string') {
			if (!publicPages.includes(to.name)) {
				next({ name: 'Login', query: to.query });
			}
		} else {
			// special cases when path has no name
			if (to.path === '/') {
				// somehow root path(/) cannot be matched, thus the name is not "home" but null
				// TODO: investigate and fix this (it works in admin portal)
				next({ name: 'Login', query: to.query });
			}
		}
	}

	// fallback to default
	next();
});

export default router;
